import { createAsyncThunk } from '@reduxjs/toolkit'
import { Auth } from '@zocks-communications/client-sdk-js'
import { ACCOUNT_ID_NAME, BI_DASHBOARD_URL, BI_TOKEN_NAME } from '../../common/constants'
import { AuthenticatedUser, AuthenticatedUserTokenDetails } from '../../models/User'
import { Authorization } from '../../models/Authorization'
import {
  BiDashboardsResponse,
  confirmForgotPassword,
  confirmRegistrationByAccountId,
  confirmSignUpWithVerificationCodeConsole,
  crossAccountLoginRequest,
  DashboardFilter,
  forgotPassword,
  getBiDashboardsByAccountId,
  getConsoleTokenForAssociationCode,
  loginWithMFATokenConsoleV2,
  loginWithUsernamePasswordByAccountId,
  loginWithUsernamePasswordConsoleV2,
  refreshTokenRequest,
  signUpWithUsernamePasswordConsoleV2,
} from '../../api/auth'
import { saveToken, Token } from '../../common/token'
import { MFAChallengeResponse } from '../../models/MFA'

export const loginWithUsernamePassword = createAsyncThunk(
  'auth/loginWithUsernamePassword',
  async ({ accountId, username, password }: { accountId: string; username: string; password: string }, thunkAPI) => {
    return loginWithUsernamePasswordByAccountId(accountId, username, password)
      .then((data: any) => {
        if ('challengeName' in data) {
          return { name: data.challengeName, session: data.session }
        }
        return parseUser(data)
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const loginWithUsernamePasswordV2 = createAsyncThunk(
  'auth/loginWithUsernamePasswordV2',
  async ({ email, password }: { email: string; password: string }, thunkAPI) => {
    return loginWithUsernamePasswordConsoleV2(email, password)
      .then((data: any) => {
        if ('message' in data) {
          return { message: data.message }
        }
        return parseUser(data)
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const loginWithMFATokenV2 = createAsyncThunk(
  'auth/loginWithMFATokenV2',
  async ({ token, challengeResponse }: { token: string; challengeResponse: MFAChallengeResponse }, thunkAPI) => {
    return loginWithMFATokenConsoleV2(token, challengeResponse)
      .then((data: any) => {
        if ('message' in data) {
          return { message: data.message }
        }
        return parseUser(data)
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async ({ accountId, refreshToken }: { accountId: string; refreshToken: string }, thunkAPI) => {
    return refreshTokenRequest(accountId, refreshToken)
      .then((data: any) => {
        if ('message' in data) {
          return { message: data.message }
        }
        const { tokenDetails, user } = parseUser(data)
        return { tokenDetails, user }
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const crossAccountLogin = createAsyncThunk(
  'auth/crossAccountLogin',
  async ({ targetAccountId, token }: { targetAccountId: string; token: string }, thunkAPI) => {
    return crossAccountLoginRequest(targetAccountId, token)
      .then((data: any) => {
        if ('message' in data) {
          return { message: data.message }
        }
        const { tokenDetails, user } = parseUser(data)
        return { tokenDetails, user }
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const getTokenForAssociationCode = createAsyncThunk(
  'auth/getTokenForAssociationCode',
  async (
    {
      code,
    }: {
      code: string
    },
    thunkAPI,
  ) => {
    return getConsoleTokenForAssociationCode(code)
      .then((data: any) => {
        const { onboarding_enabled: onboardingEnabled, onboarding_code: onboardingCode, message } = data
        const { user, tokenDetails } = parseUser(data)
        return { user, tokenDetails, onboardingEnabled, onboardingCode, message }
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const signUpWithUsernamePasswordV2 = createAsyncThunk(
  'auth/signUpWithUsernamePasswordV2',
  async ({ email, password, state }: { email: string; password: string; state: string | null }, thunkAPI) => {
    return signUpWithUsernamePasswordConsoleV2(email, password, state)
      .then((data: any) => {
        return data
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const confirmSignUpWithVerificationCode = createAsyncThunk(
  'auth/confirmSignUpWithVerificationCode',
  async ({ email, code }: { email: string; code: string }, thunkAPI) => {
    return confirmSignUpWithVerificationCodeConsole(email, code)
      .then((data: any) => {
        const { onboarding_enabled: onboardingEnabled, onboarding_code: onboardingCode, message } = data
        const { user, tokenDetails } = parseUser(data)
        return { user, tokenDetails, onboardingEnabled, onboardingCode, message }
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const forgotPasswordWithCode = createAsyncThunk(
  'auth/forgotPasswordWithCode',
  async (
    {
      email,
    }: {
      email: string
    },
    thunkAPI,
  ) => {
    return forgotPassword(email).catch((error) => {
      return thunkAPI.rejectWithValue(error)
    })
  },
)

export const confirmForgotPasswordWithCode = createAsyncThunk(
  'auth/confirmForgotPasswordWithCode',
  async ({ email, password, code }: { email: string; password: string; code: string }, thunkAPI) => {
    return confirmForgotPassword(email, password, code).catch((error) => {
      return thunkAPI.rejectWithValue(error)
    })
  },
)

export const confirmRegistration = createAsyncThunk(
  'auth/confirmRegistration',
  async (
    {
      accountId,
      username,
      newPassword,
      session,
    }: {
      accountId: string
      username: string
      newPassword: string
      session: string
    },
    thunkAPI,
  ) => {
    return confirmRegistrationByAccountId(accountId, username, newPassword, session)
      .then((data: any) => {
        return parseUser(data)
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

export const parseUser = (payload: any): { user: AuthenticatedUser; tokenDetails: AuthenticatedUserTokenDetails } => {
  const token = Token.fromPayload(payload)
  saveToken(token)
  const { user, tokenDetails } = createUserFromToken(token)
  window.localStorage.setItem(ACCOUNT_ID_NAME, user.accountId)
  return { user, tokenDetails }
}

export const createUserFromToken = (token: Token) => {
  const parsedToken = Auth.parseJwt(token.accessToken)
  const user = {
    id: parsedToken?.sub ?? '',
    accountId: parsedToken?.account_id,
    firstName: parsedToken?.firstName ?? '',
    lastName: parsedToken?.lastName ?? '',
    username: parsedToken?.username ?? '',
    name: parsedToken?.name ?? '',
    email: parsedToken?.email ?? '',
    permissions: parsedToken.authorizations
      .filter((auth: Authorization) => auth.effect === 'allow')
      .map((auth: Authorization) => auth.scopes)
      .flat(),
    phoneNumber: parsedToken?.phoneNumber ?? '',
    roles: parsedToken?.roles ?? [],
    identities: [],
    createdAt: parsedToken?.createdAt,
    displayName: parsedToken?.displayName,
    isGhosted: parsedToken.token_use === 'ghost_access',
    ghostedBy: parsedToken.ghostUserId,
    externalInfos: [],
  }
  const tokenDetails = {
    token: token.toSerialized(),
    tokenExpiration: parsedToken?.exp,
  }
  return { user, tokenDetails }
}

export const getBiDashboards = createAsyncThunk(
  'auth/getBiDashboards',
  async (
    {
      accountId,
      filters = undefined,
      dashboardIds = undefined,
    }: { accountId: string; filters?: Array<DashboardFilter> | null; dashboardIds?: Array<string> | null },
    thunkAPI,
  ) => {
    return getBiDashboardsByAccountId(accountId, filters, dashboardIds)
      .then((data) => {
        const biDashboardsResponse = data as unknown as BiDashboardsResponse
        if (biDashboardsResponse && biDashboardsResponse.biToken) {
          storeBIData(biDashboardsResponse.biToken)
        }
        return biDashboardsResponse.dashboards || []
      })
      .catch((error) => {
        return thunkAPI.rejectWithValue(error)
      })
  },
)

// export const inviteConsoleUser = createAsyncThunk(
//   'auth/inviteConsoleUser',
//   async ({ accountId, payload }: { accountId: string; payload: ConsoleUser }, thunkAPI) => {
//     return inviteConsoleUserByAccountId(accountId, payload)
//       .then((response) => {
//         return response
//       })
//       .catch((reason: ErrorResponse) => {
//         return thunkAPI.rejectWithValue(reason)
//       })
//   },
// )

const storeBIData = (token: string) => {
  const biToken = Auth.parseJwt(token)
  window.localStorage.setItem(BI_TOKEN_NAME, token)
  window.localStorage.setItem(BI_DASHBOARD_URL, biToken.aud)
}
